/**
 * Dreamer latency instrumentation. One performance mark per outbound Dreamer
 * request and one measure on its matching response let the QA harness read
 * client-side round-trip time via performance.getEntriesByName, keyed on the
 * same requestId the server logs — the only segment of the Dreamer path that
 * had no timing signal.
 */

const REQUEST_MARK_PREFIX = "dreamer-request-";
const RESPONSE_MEASURE_PREFIX = "dreamer-response-";

// Completed measures stay in the User Timing buffer until cleared. Retain a
// window wide enough for the QA harness to read recent round trips, and drop
// the oldest beyond it so a long-lived session doesn't grow the timeline
// unboundedly.
const MAX_RETAINED_MEASURES = 50;

// A request mark is cleared when its response arrives (measureResponseReceived)
// or its request is cancelled (clearRequestMark). Neither fires for a request
// that is simply abandoned — the WebSocket errors, the component unmounts, or
// the server never replies — so marks older than this are swept on every new
// send as a backstop against leaking them for the life of the session.
const MARK_TTL_MS = 5 * 60 * 1000;

const requestMarkName = (requestId: string): string =>
  `${REQUEST_MARK_PREFIX}${requestId}`;

const responseMeasureName = (requestId: string): string =>
  `${RESPONSE_MEASURE_PREFIX}${requestId}`;

const trimRetainedMeasures = (): void => {
  const dreamerMeasures = performance
    .getEntriesByType("measure")
    .filter((entry) => entry.name.startsWith(RESPONSE_MEASURE_PREFIX));
  const staleCount = dreamerMeasures.length - MAX_RETAINED_MEASURES;
  for (const staleMeasure of dreamerMeasures.slice(0, Math.max(staleCount, 0))) {
    performance.clearMeasures(staleMeasure.name);
  }
};

const trimStaleMarks = (): void => {
  const now = performance.now();
  const dreamerMarks = performance
    .getEntriesByType("mark")
    .filter((entry) => entry.name.startsWith(REQUEST_MARK_PREFIX));
  for (const staleMark of dreamerMarks) {
    if (now - staleMark.startTime > MARK_TTL_MS) {
      performance.clearMarks(staleMark.name);
    }
  }
};

export const markRequestSent = (requestId: string): void => {
  trimStaleMarks();
  performance.mark(requestMarkName(requestId));
};

export const measureResponseReceived = (requestId: string): void => {
  const markName = requestMarkName(requestId);
  // performance.measure throws when the start mark is missing (another
  // consumer can clear the buffer) — a lost measure must not break message
  // handling.
  if (performance.getEntriesByName(markName, "mark").length === 0) {
    return;
  }
  performance.measure(responseMeasureName(requestId), markName);
  performance.clearMarks(markName);
  trimRetainedMeasures();
};

export const clearRequestMark = (requestId: string): void => {
  performance.clearMarks(requestMarkName(requestId));
};

// Shared by the WebSocket error handler and the provider-unmount cleanup:
// both abandon every currently pending request's mark, but for different
// reasons and with different consequences for the pending-request set
// itself, so this only clears marks — it never touches that set.
export const clearRequestMarks = (requestIds: Iterable<string>): void => {
  for (const requestId of requestIds) {
    clearRequestMark(requestId);
  }
};
